import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import Icon1 from "@assets/icons/Icon";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useDataStore } from "@store/user_org";
import { usePlanningStore, getCategories } from "@store/planningStore";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import type { PendingAPrevoir } from "@services/api/ordres";
import PendingAPrevoirPanel from "@components/ui/PendingAPrevoirPanel";
import type { OrdreIntervention } from "types/ordre";
import { appointmentsApi } from "@services/api/appointments";
import { bookingsApi, type SlotValidationResult } from "@services/api/bookings";
import ForceConfirmModal from "@components/modals/ForceConfirmModal";
import InternalBookingPage from "@components/InternalBookingPage";
import { vehiclesApi } from "@services/api/vehicles";
import { isOwnerOrAdmin } from "@utils/permissions";
import { searchDossiersFull, type ProjectHit } from "@services/algolia";
import { showNotification } from "context/notification.service";
import CheckBox from "@components/ui/selection/CheckBox";
import { formatTime, hourToTimeStr, timeStrToHour, GRID_HOUR_START, GRID_HOUR_END, isoToSeconds, secondsToIso } from "@utils/timeGrid";
import { getReceptionKind } from "types/org";
import type { AppointmentCategory, Appointment } from "types/garage/planning";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import type { ClientVehicle } from "types/client_vehicle";

type ApptType = "repair" | "location" | "interne";

interface VehicleSuggestion {
  plate: string;
  marque: string;
  modele: string;
  vehicle_id?: string;
  dossier_id?: string;
  client_name?: string;
  image_url?: string;
}

export type QuickCreateResult =
  | { type: "repair"; ordreId: string }
  | { type: "location"; reservation: VehicleReservation }
  | { type: "interne"; appointment: Appointment };

interface QuickAppointmentModalProps {
  date: string;
  startHour: number;
  category: AppointmentCategory;
  defaultType?: ApptType;
  onClose: () => void;
  onCreated: (result: QuickCreateResult) => void;
}

function isoFromDate(date: string): number {
  const d = new Date(date + "T12:00:00");
  return Math.floor(d.getTime() / 1000);
}

function isoFromSeconds(s: number): string {
  const d = new Date(s * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function QuickAppointmentModal({
  date,
  startHour: initialStartHour,
  category,
  defaultType,
  onClose,
  onCreated,
}: QuickAppointmentModalProps) {
  // Start hour is seeded from the clicked slot but stays editable in the form.
  const [startHour, setStartHour] = useState<number>(initialStartHour);
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);

  const allCategories = useMemo(() => getCategories(orgData), [orgData]);
  const allReceptions = useMemo(() => orgData?.receptions ?? [], [orgData?.receptions]);
  const kindOf = useCallback(
    (catId: string) => {
      const rec = allReceptions.find((r) => r.id === catId);
      return rec ? getReceptionKind(rec) : "reception";
    },
    [allReceptions],
  );

  const inferredType: ApptType = useMemo(() => {
    if (defaultType) return defaultType;
    const k = kindOf(category);
    if (k === "location" || k === "pret") return "location";
    if (k === "divers") return "interne";
    return "repair";
  }, [defaultType, category, kindOf]);

  const [apptType, setApptType] = useState<ApptType>(inferredType);
  // A "Client" RDV uses the exact same booking flow as a client (BookingWidgetV3),
  // with an internal client search (no RGPD). Seeded open when the clicked slot is a
  // reception/client column.
  const [showClientBooking, setShowClientBooking] = useState(false);
  // The inline repair quick-form is superseded by the full client flow (handoff →
  // BookingWidgetV3). Kept rendered-off so its handlers, shared with the Location
  // form, stay referenced.
  const SHOW_LEGACY_REPAIR_FORM = false;
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [saving, setSaving] = useState(false);
  const [forceConfirm, setForceConfirm] = useState<SlotValidationResult | null>(null);

  // Filter categories compatible with the selected type
  const compatibleCategories = useMemo(() => {
    return allCategories.filter((c) => {
      const k = kindOf(c.id);
      if (apptType === "repair") return k === "reception" || k === "carrosserie";
      if (apptType === "location") return k === "location" || k === "pret";
      return k === "divers" || k === "pret";
    });
  }, [allCategories, apptType, kindOf]);

  // When type changes, ensure selectedCategory is compatible (fallback to first)
  useEffect(() => {
    if (compatibleCategories.length === 0) return;
    if (!compatibleCategories.some((c) => c.id === selectedCategory)) {
      setSelectedCategory(compatibleCategories[0].id);
    }
  }, [apptType, compatibleCategories, selectedCategory]);

  // -------- Shared: client/plate/phone state --------
  const [clientName, setClientName] = useState("");
  const [plate, setPlate] = useState("");
  const [phone, setPhone] = useState("");

  const [dossierResults, setDossierResults] = useState<ProjectHit[]>([]);
  const [selectedDossierId, setSelectedDossierId] = useState<string | null>(null);

  // Client's vehicle fleet ("parc") — loaded when a client is selected by name/phone,
  // so a vehicle can be picked without knowing the plate.
  const [clientVehicles, setClientVehicles] = useState<ClientVehicle[]>([]);
  const [selectedPending, setSelectedPending] = useState<PendingAPrevoir[]>([]);

  const [phoneLookupName, setPhoneLookupName] = useState<string | null>(null);

  const [vehicleInfo, setVehicleInfo] = useState<{
    vehicle_id?: string;
    marque?: string;
    modele?: string;
    image_url?: string;
  } | null>(null);

  const [vehicleSuggestions, setVehicleSuggestions] = useState<VehicleSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [plateAutoFilled, setPlateAutoFilled] = useState(false);

  // -------- Location-specific state --------
  const [locationVehicles, setLocationVehicles] = useState<Vehicle[]>([]);
  const [locationVehicleId, setLocationVehicleId] = useState<string>("");
  const [locStartDate, setLocStartDate] = useState<string>(date);
  const [locEndDate, setLocEndDate] = useState<string>(date);
  const [locStartHour, setLocStartHour] = useState<number>(startHour);
  const [locEndHour, setLocEndHour] = useState<number>(Math.min(startHour + 2, 19));
  const [locKmDepart, setLocKmDepart] = useState("");
  const [locNotes, setLocNotes] = useState("");

  // Load vehicles for location based on column kind
  useEffect(() => {
    if (apptType !== "location" || !orgData?.id) return;
    const k = kindOf(selectedCategory);
    const usage = k === "pret" ? "pret" : "location";
    vehiclesApi
      .list({ org_id: orgData.id, usage })
      .then(setLocationVehicles)
      .catch(() => setLocationVehicles([]));
  }, [apptType, orgData?.id, selectedCategory, kindOf]);

  // -------- Interne-specific state --------
  const [interneTitle, setInterneTitle] = useState("");
  const [interneEndHour, setInterneEndHour] = useState<number>(Math.min(startHour + 1, 19));
  const [interneAllDay, setInterneAllDay] = useState(false);
  const [interneEndDate, setInterneEndDate] = useState<string>(date); // inclusive end day for multi-day blocks

  // -------- Search/lookup helpers (shared with repair) --------
  const handleSearchDossier = async (query: string) => {
    if (!orgData?.id || query.length < 2) {
      setDossierResults([]);
      return;
    }
    try {
      const results = await searchDossiersFull(query, orgData.id);
      setDossierResults(results);
    } catch {
      setDossierResults([]);
    }
  };

  const handleClientNameChange = (v: string) => {
    setClientName(v);
    setSelectedDossierId(null);
    setPhoneLookupName(null);
    setClientVehicles([]);
    handleSearchDossier(v);
  };

  // Fill the vehicle from the client's fleet (no plate typing needed).
  const handleSelectClientVehicle = useCallback((v: ClientVehicle) => {
    setPlate(v.plate);
    setVehicleInfo({
      vehicle_id: v.id,
      marque: v.marque,
      modele: v.modele,
      image_url: v.image_url,
    });
    setVehicleSuggestions([]);
    setShowSuggestions(false);
    // The client was chosen deliberately by name — keep it even if the plate is edited.
    setPlateAutoFilled(false);
  }, []);

  // Load the client's vehicle fleet so it can be picked without the plate,
  // and pre-fill the phone from the dossier when it isn't set yet.
  const loadClientParc = useCallback(
    async (dossierId: string) => {
      setClientVehicles([]);
      try {
        const dossier = await dossiersApi.get(dossierId);
        const vehicles = dossier.vehicles ?? [];
        setClientVehicles(vehicles);
        // The client's fleet is shown so a vehicle can be picked (or a new plate
        // typed). Select the first one by default so the RDV can be validated
        // straight away without retyping the plate.
        if (vehicles.length > 0) {
          handleSelectClientVehicle(vehicles[0]);
        }
        const dossierPhone =
          dossier.phone ||
          dossier.contacts?.find((c) => c.is_primary)?.phone ||
          dossier.contacts?.find((c) => c.phone)?.phone;
        if (dossierPhone) {
          setPhone((prev) => (prev.trim() ? prev : dossierPhone));
        }
      } catch {
        setClientVehicles([]);
      }
    },
    [handleSelectClientVehicle],
  );

  const handleSelectDossier = (hit: ProjectHit) => {
    setSelectedDossierId(hit.value);
    setClientName(hit.label);
    setDossierResults([]);
    loadClientParc(hit.value);
  };

  const searchVehicles = useCallback(
    async (query: string) => {
      if (!orgData?.id) return;
      const raw = query.replace(/[^A-Za-z0-9]/g, "");
      if (raw.length < 2) {
        setVehicleSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      try {
        const results = await bookingsApi.searchVehicles(raw, orgData.id);
        setVehicleSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch {
        setVehicleSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [orgData?.id],
  );

  const handleSelectVehicle = (suggestion: VehicleSuggestion) => {
    setPlate(suggestion.plate);
    setVehicleInfo({
      vehicle_id: suggestion.vehicle_id,
      marque: suggestion.marque,
      modele: suggestion.modele,
      image_url: suggestion.image_url,
    });
    setVehicleSuggestions([]);
    setShowSuggestions(false);

    if (suggestion.dossier_id) {
      setSelectedDossierId(suggestion.dossier_id);
      if (suggestion.client_name) setClientName(suggestion.client_name);
      setPlateAutoFilled(true);
    }
  };

  const handlePlateChange = async (v: string) => {
    setPlate(v);
    const raw = v.replace(/[^A-Za-z0-9]/g, "");

    if (plateAutoFilled) {
      setSelectedDossierId(null);
      setClientName("");
      setPhone("");
      setPhoneLookupName(null);
      setPlateAutoFilled(false);
    }
    setVehicleInfo(null);

    if (raw.length >= 2 && raw.length < 7) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        searchVehicles(v);
      }, 300);
    } else {
      setVehicleSuggestions([]);
      setShowSuggestions(false);
    }

    if (raw.length === 7 && orgData?.id) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setVehicleSuggestions([]);
      setShowSuggestions(false);
      try {
        const result = await bookingsApi.lookupVehicle(raw, orgData.id);
        if (result.found) {
          setVehicleInfo({
            vehicle_id: result.vehicle_id,
            marque: result.marque,
            modele: result.modele,
            image_url: result.image_url,
          });
          if (result.dossier_id) {
            setSelectedDossierId(result.dossier_id);
            if (result.client_name) setClientName(result.client_name);
            if (result.client_phone) setPhone(result.client_phone);
            setPlateAutoFilled(true);
          }
        }
      } catch {
        // lookup failed
      }
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handlePhoneChange = async (v: string) => {
    setPhone(v);
    const digits = v.replace(/\D/g, "");
    if (digits.length === 10 && orgData?.id) {
      try {
        const result = await bookingsApi.lookupClient(digits, orgData.id);
        if (result.found) {
          const fullName = [result.prenom, result.nom].filter(Boolean).join(" ");
          if (fullName) setClientName(fullName);
          if (result.dossier_id) {
            setSelectedDossierId(result.dossier_id);
            loadClientParc(result.dossier_id);
          }
          setPhoneLookupName(fullName || null);
        }
      } catch {
        // lookup failed silently
      }
    } else {
      setPhoneLookupName(null);
    }
  };

  // -------- Save handlers --------
  const savingRef = useRef(false);

  const refreshPlanning = () => {
    const planningState = usePlanningStore.getState();
    if (planningState._fetchedOrgId) {
      planningState.fetchAppointments(planningState._fetchedOrgId, true);
    }
  };

  const handleSaveRepair = async (force = false) => {
    if (!orgData?.id || !clientName.trim()) return;

    // Validate the reception slot with the SAME rules as the online widget (staff can force)
    if (!force) {
      const hh = Math.floor(startHour);
      const mm = Math.floor((startHour - hh) * 60 + 1e-9);
      const depositTime = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
      try {
        const result = await bookingsApi.validateSlot({
          deposit_date: date,
          deposit_time: depositTime,
          service_type: "Mécanique",
          check_deposit: true,
        });
        if (result.errors.length > 0) {
          savingRef.current = false; // allow re-save after the dialog
          setForceConfirm(result);
          return;
        }
      } catch (e) {
        // Fail-open: if validation is unreachable, don't block the staff save
        console.error("validateSlot failed:", e);
      }
    }

    let dossierId = selectedDossierId;

    if (!dossierId) {
      const now = { seconds: Math.floor(Date.now() / 1000) };
      const dossier = await dossiersApi.create({
        org_id: orgData.id,
        name: clientName.trim(),
        type: "particulier",
        tags: [],
        phone: phone.replace(/\s/g, "") || undefined,
        created_at: now,
      } as any);
      dossierId = dossier.id;
    }

    const now = { seconds: Math.floor(Date.now() / 1000) };
    const receptionDate = new Date(date + "T00:00:00");
    const hours = Math.floor(startHour);
    const minutes = Math.floor((startHour - hours) * 60 + 1e-9);
    receptionDate.setHours(hours, minutes, 0, 0);
    const receptionSeconds = Math.floor(receptionDate.getTime() / 1000);

    // Re-proposed "à prévoir" lines (saved on a previous visit) join the new RDV as
    // fresh, included interventions.
    const reproposed: OrdreIntervention[] = selectedPending.map((p, idx) => ({
      id: `i-${Date.now()}-${idx}`,
      label: p.intervention.label,
      estimated_hours: p.intervention.estimated_hours,
      ...(p.intervention.forfait_code ? { forfait_code: p.intervention.forfait_code } : {}),
      group: "initial",
      status: "inclus",
    }));

    const ordre = await ordresApi.create({
      org_id: orgData.id,
      dossier_id: dossierId,
      name: clientName.trim(),
      num: `TEMP-${crypto.randomUUID().slice(0, 6).toUpperCase()}`,
      status: "a_preparer",
      immatriculation: plate || undefined,
      vehicle_id: vehicleInfo?.vehicle_id || undefined,
      assigned_to: userData?.id,
      reception_date: { seconds: receptionSeconds },
      tags: [],
      interventions: reproposed,
      documents: [],
      created_at: now,
      ...(force ? { force: true } : {}),
    } as any);

    // Stamp the originals as consumed so they leave the pending pool (kept as history).
    if (selectedPending.length > 0) {
      try {
        await ordresApi.consumeAPrevoir(ordre.id, selectedPending.map((p) => ({ ordre_id: p.ordre_id, intervention_id: p.intervention.id })));
      } catch (e) {
        console.error("Error consuming à-prévoir lines:", e);
      }
    }

    // Backend _sync_appointments creates the reception appointment. Update its category if needed.
    const defaultRecId = orgData?.receptions?.[0]?.id || "reception_1";
    if (selectedCategory !== defaultRecId) {
      try {
        const appts = await appointmentsApi.list({ org_id: orgData.id, date });
        const created = appts.find((a) => a.ordre_id === ordre.id);
        if (created) {
          await appointmentsApi.update(created.id, { category: selectedCategory });
        }
      } catch {
        // category update failed, not critical
      }
    }
    refreshPlanning();
    showNotification({ message: "Rendez-vous créé avec succès", type: "success" });
    onCreated({ type: "repair", ordreId: ordre.id });
    onClose();
  };

  const handleSaveLocation = async () => {
    if (!orgData?.id || !clientName.trim() || !locationVehicleId) return;
    const sameDay = locStartDate === locEndDate;
    const durationHours = sameDay ? Math.max(0.25, locEndHour - locStartHour) : undefined;

    const reservation = await vehiclesApi.createReservation({
      org_id: orgData.id,
      vehicleId: locationVehicleId,
      clientName: clientName.trim(),
      startDate: locStartDate,
      endDate: locEndDate,
      hasPickup: true,
      hasReturn: true,
      startHour: locStartHour,
      durationHours,
      returnHour: sameDay ? undefined : locEndHour,
      clientPhone: phone.replace(/\s/g, "") || undefined,
      dossierId: selectedDossierId || undefined,
      kmDepart: locKmDepart || undefined,
      notes: locNotes || undefined,
    });

    // Create the linked appointment on the planning
    const apptDuration = sameDay ? durationHours! : Math.max(0.25, 19 - locStartHour);
    const vehicleLabel = locationVehicles.find((v) => v.id === locationVehicleId);
    const plateLabel = vehicleLabel?.plate || "";

    const created = await appointmentsApi.create({
      org_id: orgData.id,
      clientName: clientName.trim(),
      plate: plateLabel,
      category: selectedCategory,
      date: locStartDate,
      startHour: locStartHour,
      durationHours: apptDuration,
      hasCarIcon: true,
      hasMailIcon: false,
      reservation_id: reservation.id,
      dossier_id: selectedDossierId || undefined,
      assigned_to: userData?.id,
    } as any);

    refreshPlanning();
    showNotification({ message: "Location créée avec succès", type: "success" });
    onCreated({ type: "location", reservation });
    onClose();
    void created;
  };

  const handleSaveInterne = async () => {
    if (!orgData?.id || !interneTitle.trim()) return;

    // Full-day blocks span the whole visible grid and live in the all-day banner.
    // endDate (>= start date) makes the block span multiple days.
    const allDay = interneAllDay;
    const effectiveStart = allDay ? GRID_HOUR_START : startHour;
    const durationHours = allDay
      ? GRID_HOUR_END - GRID_HOUR_START
      : Math.max(0.25, interneEndHour - startHour);
    const endDate = allDay && interneEndDate > date ? interneEndDate : date;

    const created = await appointmentsApi.create({
      org_id: orgData.id,
      clientName: interneTitle.trim(),
      plate: "",
      category: selectedCategory,
      date,
      startHour: effectiveStart,
      durationHours,
      allDay,
      endDate,
      hasCarIcon: false,
      hasMailIcon: false,
      assigned_to: userData?.id,
    } as any);

    refreshPlanning();
    showNotification({ message: "Bloc créé", type: "success" });
    onCreated({ type: "interne", appointment: created });
    onClose();
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (apptType === "repair") await handleSaveRepair();
      else if (apptType === "location") await handleSaveLocation();
      else await handleSaveInterne();
    } catch {
      showNotification({ message: "Erreur lors de la création", type: "error" });
      savingRef.current = false;
    }
    setSaving(false);
  };

  // -------- Validation --------
  // A repair RDV requires a vehicle: either a plate or a vehicle picked from a dossier
  const hasVehicle = plate.trim().length > 0 || !!vehicleInfo?.vehicle_id;

  const canSave = useMemo(() => {
    if (saving) return false;
    if (apptType === "repair") return clientName.trim().length > 0 && hasVehicle;
    if (apptType === "location") return clientName.trim().length > 0 && !!locationVehicleId;
    return interneTitle.trim().length > 0;
  }, [apptType, clientName, locationVehicleId, interneTitle, saving, hasVehicle]);

  const dateObj = new Date(date + "T00:00:00");
  const dateLabel = dateObj.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

  const categoryOptions = compatibleCategories.map((c) => ({ value: c.id, label: c.label }));
  const vehicleOptions = locationVehicles.map((v) => ({
    value: v.id,
    label: `${v.name} — ${v.plate}`,
  }));

  const titleLabel =
    apptType === "repair"
      ? "Nouveau rendez-vous"
      : apptType === "location"
        ? "Nouvelle location"
        : "Nouveau bloc";

  // "Client" RDV → exact same flow as a client, with internal client search.
  if (showClientBooking) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ background: "rgba(0,0,0,0.4)" }}
      >
        <InternalBookingPage
          internalMode
          isManager={isOwnerOrAdmin(userData)}
          onClose={onClose}
          onSuccess={() => {
            if (orgData?.id) usePlanningStore.getState().fetchAppointments(orgData.id, true);
            onClose();
          }}
        />
      </div>
    );
  }

  return (
    <BaseModal
      title={titleLabel}
      startIcon={<Icon1 name="horloge" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Type segmented control */}
        <div className="flex flex-row p-0.5 bg-neutral-100 rounded-lg gap-0.5">
          {(
            [
              { id: "repair", label: "Client" },
              { id: "location", label: "Location" },
              { id: "interne", label: "Interne" },
            ] as { id: ApptType; label: string }[]
          ).map((opt) => {
            const isActive = apptType === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  setApptType(opt.id);
                  setShowClientBooking(opt.id === "repair");
                }}
                className={`flex-1 px-3 py-1.5 rounded-md text-h3 font-medium transition-colors ${
                  isActive
                    ? "bg-white text-neutral-800 shadow-sm"
                    : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Date/time info — hidden for Client RDV (the full flow picks its own slot) */}
        {apptType !== "repair" && (
          <div className="flex flex-row items-center gap-2 p-3 bg-indigo-50 rounded-lg">
            <Icon1 name="horloge" size={16} color="#6366F1" />
            <span className="text-h3 font-medium text-indigo-700">
              {apptType === "interne" && interneAllDay
                ? interneEndDate > date
                  ? `Du ${dateLabel} au ${new Date(interneEndDate + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}`
                  : `${dateLabel} — journée complète`
                : `${dateLabel} — ${formatTime(startHour)}`}
            </span>
          </div>
        )}

        {/* ============ Client RDV — handoff to the full client booking flow ============ */}
        {apptType === "repair" && (
          <div className="flex flex-col gap-3 p-4 rounded-lg bg-indigo-50 border border-indigo-100">
            <span className="text-h3 text-neutral-600">
              Le RDV client utilise la même prise de RDV que les clients, avec recherche par
              immatriculation, nom ou téléphone (sans contrainte RGPD).
            </span>
            <Button
              variant="small"
              label="Ouvrir la prise de RDV client →"
              onClick={() => setShowClientBooking(true)}
            />
          </div>
        )}

        {/* Legacy inline repair quick-form — kept (gated off) so its handlers, shared
            with the Location form, stay referenced. The Client RDV now uses the full
            client flow (BookingWidgetV3) via the handoff above. */}
        {SHOW_LEGACY_REPAIR_FORM && apptType === "repair" && (
          <>
            <div className="relative">
              <TextField
                label="Nom du client"
                value={clientName}
                onChange={handleClientNameChange}
                placeholder="Rechercher ou créer..."
              />
              {dossierResults.length > 0 && !selectedDossierId && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-200 overflow-y-auto">
                  <div className="px-3 py-1.5 text-h4 text-neutral-400 border-b border-neutral-100">
                    Dossiers existants
                  </div>
                  {dossierResults.map((hit) => (
                    <button
                      key={hit.value}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors"
                      onClick={() => handleSelectDossier(hit)}
                    >
                      <div className="text-h3 font-medium text-neutral-800">{hit.label}</div>
                      {hit.extra_label && (
                        <div className="text-h4 text-neutral-400">{hit.extra_label}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {selectedDossierId && (
                <div className="flex flex-row items-center gap-1 mt-1">
                  <Icon1 name="done2" size={12} color="#22C55E" />
                  <span className="text-h4 text-green-600">Dossier existant sélectionné</span>
                </div>
              )}
            </div>

            {/* Client fleet ("parc") — pick a vehicle without knowing the plate.
                Stays visible after selection so another vehicle can be chosen;
                a new vehicle is added by typing its plate in the field below. */}
            {clientVehicles.length > 0 && (
              <div className="flex flex-col gap-1">
                <span className="text-h4 text-neutral-400">
                  Véhicules du client ({clientVehicles.length})
                </span>
                <div className="flex flex-col gap-1.5 max-h-200 overflow-y-auto">
                  {clientVehicles.map((v) => {
                    const isSelected = vehicleInfo?.vehicle_id === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 border rounded-lg transition-colors flex flex-row items-center gap-3 ${
                          isSelected
                            ? "border-indigo-400 bg-indigo-50"
                            : "border-neutral-200 hover:bg-neutral-50"
                        }`}
                        onClick={() => handleSelectClientVehicle(v)}
                      >
                        {v.image_url && (
                          <img
                            src={v.image_url}
                            alt={v.plate}
                            className="w-35 h-25 object-contain rounded flex-shrink-0"
                          />
                        )}
                        <div className="flex flex-col min-w-0">
                          <span className="text-h3 font-semibold text-neutral-800">{v.plate}</span>
                          <span className="text-h4 text-neutral-500">
                            {[v.marque, v.modele].filter(Boolean).join(" ")}
                          </span>
                        </div>
                        {isSelected && (
                          <Icon1 name="done2" size={16} color="#6366F1" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <span className="text-h4 text-neutral-400">
                  ou saisissez une autre immatriculation ci-dessous pour ajouter un véhicule.
                </span>
              </div>
            )}

            <div className="relative">
              <TextField
                label="Immatriculation"
                type="plate"
                value={plate}
                onChange={handlePlateChange}
                placeholder="AA-123-BB"
              />
              {showSuggestions && vehicleSuggestions.length > 0 && !vehicleInfo && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-200 overflow-y-auto">
                  <div className="px-3 py-1.5 text-h4 text-neutral-400 border-b border-neutral-100">
                    Véhicules correspondants
                  </div>
                  {vehicleSuggestions.map((s) => (
                    <button
                      key={`${s.plate}-${s.vehicle_id || s.dossier_id}`}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors flex flex-row items-center gap-3"
                      onClick={() => handleSelectVehicle(s)}
                    >
                      {s.image_url && (
                        <img
                          src={s.image_url}
                          alt={s.plate}
                          className="w-35 h-25 object-contain rounded flex-shrink-0"
                        />
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="text-h3 font-semibold text-neutral-800">{s.plate}</span>
                        <span className="text-h4 text-neutral-500">
                          {[s.marque, s.modele].filter(Boolean).join(" ")}
                        </span>
                        {s.client_name && (
                          <span className="text-h4 text-neutral-400 truncate">{s.client_name}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {vehicleInfo && (
                <div className="flex flex-row items-center gap-3 mt-1 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  {vehicleInfo?.image_url && (
                    <img
                      src={vehicleInfo?.image_url}
                      alt="véhicule"
                      className="w-50 h-35 object-contain rounded"
                    />
                  )}
                  <div className="flex flex-col">
                    <span className="text-h3 font-medium text-blue-700">
                      {[vehicleInfo?.marque, vehicleInfo?.modele].filter(Boolean).join(" ")}
                    </span>
                    <span className="text-h4 text-blue-500">Véhicule trouvé</span>
                  </div>
                </div>
              )}
              {!hasVehicle && (
                <span className="text-h4 text-red-500 mt-1 block">Un véhicule (immatriculation) est obligatoire pour valider le RDV.</span>
              )}
              {selectedDossierId && vehicleInfo?.vehicle_id && (
                <div className="mt-2">
                  <PendingAPrevoirPanel
                    dossierId={selectedDossierId}
                    vin={clientVehicles.find((v) => v.id === vehicleInfo?.vehicle_id)?.vin}
                    onSelectionChange={setSelectedPending}
                  />
                </div>
              )}
            </div>

            <div>
              <TextField
                label="Téléphone"
                type="phone"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="06 12 34 56 78"
              />
              {phoneLookupName && (
                <div className="flex flex-row items-center gap-2 mt-1 p-2 bg-green-50 rounded-lg border border-green-200">
                  <Icon1 name="done2" size={14} color="#22C55E" />
                  <span className="text-h3 font-medium text-green-700">
                    Bonjour {phoneLookupName}
                  </span>
                </div>
              )}
            </div>

            <TimePicker
              label="Heure de début"
              value={hourToTimeStr(startHour)}
              onChange={(t) => {
                if (!t) return;
                setStartHour(timeStrToHour(t));
              }}
            />
          </>
        )}

        {/* ============ Location fields ============ */}
        {apptType === "location" && (
          <>
            <DropDownSearch
              label="Véhicule"
              options={vehicleOptions}
              value={locationVehicleId}
              onSelect={(v) => setLocationVehicleId(v as string)}
              placeholder={locationVehicles.length === 0 ? "Aucun véhicule disponible" : "Choisir un véhicule..."}
            />

            <div className="relative">
              <TextField
                label="Nom du client"
                value={clientName}
                onChange={handleClientNameChange}
                placeholder="Rechercher ou créer..."
              />
              {dossierResults.length > 0 && !selectedDossierId && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-200 overflow-y-auto">
                  <div className="px-3 py-1.5 text-h4 text-neutral-400 border-b border-neutral-100">
                    Dossiers existants
                  </div>
                  {dossierResults.map((hit) => (
                    <button
                      key={hit.value}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors"
                      onClick={() => handleSelectDossier(hit)}
                    >
                      <div className="text-h3 font-medium text-neutral-800">{hit.label}</div>
                      {hit.extra_label && (
                        <div className="text-h4 text-neutral-400">{hit.extra_label}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {selectedDossierId && (
                <div className="flex flex-row items-center gap-1 mt-1">
                  <Icon1 name="done2" size={12} color="#22C55E" />
                  <span className="text-h4 text-green-600">Dossier existant sélectionné</span>
                </div>
              )}
            </div>

            <TextField
              label="Téléphone"
              type="phone"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="06 12 34 56 78"
            />

            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <DatePicker
                  label="Date début"
                  seconds={isoFromDate(locStartDate)}
                  onChange={(s) => {
                    const iso = isoFromSeconds(s);
                    setLocStartDate(iso);
                    if (iso > locEndDate) setLocEndDate(iso);
                  }}
                />
              </div>
              <div className="flex-1">
                <DatePicker
                  label="Date fin"
                  seconds={isoFromDate(locEndDate)}
                  onChange={(s) => setLocEndDate(isoFromSeconds(s))}
                  minDateSeconds={isoFromDate(locStartDate)}
                />
              </div>
            </div>

            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TimePicker
                  label="Heure départ"
                  value={hourToTimeStr(locStartHour)}
                  onChange={(t) => {
                    if (!t) return;
                    const h = timeStrToHour(t);
                    setLocStartHour(h);
                    if (locStartDate === locEndDate && h >= locEndHour) {
                      setLocEndHour(Math.min(h + 1, 19));
                    }
                  }}
                />
              </div>
              <div className="flex-1">
                <TimePicker
                  label="Heure retour"
                  value={hourToTimeStr(locEndHour)}
                  onChange={(t) => {
                    if (!t) return;
                    setLocEndHour(timeStrToHour(t));
                  }}
                />
              </div>
            </div>

            <TextField label="KM départ" value={locKmDepart} onChange={setLocKmDepart} />
            <TextField label="Notes" value={locNotes} onChange={setLocNotes} />
          </>
        )}

        {/* ============ Interne fields ============ */}
        {apptType === "interne" && (
          <>
            <TextField
              label="Motif"
              value={interneTitle}
              onChange={setInterneTitle}
              placeholder="Réunion, formation, pause, absence..."
            />

            <label className="flex flex-row items-center gap-2 cursor-pointer select-none">
              <CheckBox
                checked={interneAllDay}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setInterneAllDay(checked);
                  if (checked && interneEndDate < date) setInterneEndDate(date);
                }}
              />
              <span className="text-h3 text-neutral-700">Journée complète</span>
            </label>

            {interneAllDay ? (
              <DatePicker
                label="Jusqu'au (inclus)"
                seconds={isoToSeconds(interneEndDate)}
                minDateSeconds={isoToSeconds(date)}
                onChange={(s) => {
                  if (!s) return;
                  setInterneEndDate(secondsToIso(s));
                }}
              />
            ) : (
              <div className="flex flex-row gap-2">
                <div className="flex-1">
                  <TimePicker
                    label="Heure de début"
                    value={hourToTimeStr(startHour)}
                    onChange={(t) => {
                      if (!t) return;
                      const h = timeStrToHour(t);
                      setStartHour(h);
                      if (h >= interneEndHour) setInterneEndHour(Math.min(h + 1, GRID_HOUR_END));
                    }}
                  />
                </div>
                <div className="flex-1">
                  <TimePicker
                    label="Heure de fin"
                    value={hourToTimeStr(interneEndHour)}
                    onChange={(t) => {
                      if (!t) return;
                      setInterneEndHour(timeStrToHour(t));
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Category — shown for Location/Interne (Client RDV uses the full client flow) */}
        {apptType !== "repair" && (
          <DropDown
            label="Catégorie"
            options={categoryOptions}
            value={selectedCategory}
            onSelect={(v) => setSelectedCategory(v as AppointmentCategory)}
          />
        )}
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        {/* Client RDV is created through the full client flow (handoff above), not this footer. */}
        {apptType !== "repair" && (
          <Button
            variant="small"
            label={saving ? "Création..." : "Créer"}
            onClick={handleSave}
            disabled={!canSave}
          />
        )}
      </div>
      {forceConfirm && (
        <ForceConfirmModal
          title="Créneau non conforme"
          message={forceConfirm.errors[0]?.message || "Ce créneau ne respecte pas les règles de prise de RDV."}
          confirmLabel="Forcer ce RDV"
          onCancel={() => setForceConfirm(null)}
          onConfirm={async () => {
            setForceConfirm(null);
            setSaving(true);
            try {
              await handleSaveRepair(true);
            } catch {
              showNotification({ message: "Erreur lors de la création", type: "error" });
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
    </BaseModal>
  );
}
